import React, { useCallback } from 'react'
import { Box, Paper, Stack, Typography, useTheme } from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import { format } from '@e3dc-react/shell/Libs/date'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { BarChart, Bar, YAxis, XAxis, CartesianGrid, Tooltip, TooltipProps, Legend, ResponsiveContainer } from 'recharts'
import { getLocalizedNumber } from 'src/Libs/languages/functions/default'
import { UnitTypes } from 'src/Libs/types'

const useStyles = makeStyles()(theme => ({
  tooltipIndicator: {
    width: 8,
    height: 8,
    alignSelf: 'center',
  },
  tooltipIndicatorTotal: {
    backgroundColor: theme.palette.chart.totalGridChargedBarV1,
  },
  tooltipIndicatorSolar: {
    backgroundColor: theme.palette.chart.solarChargedBarV1,
  },
  tooltipIndicatorDischarge: {
    backgroundColor: theme.palette.chart.totalDischargedEnergyBackgroundV1,
  },
}))

interface ChargingSessionChartProps {
  data: { total: number; solar: number; discharged: number; date: string }[]
}

const ChargingSessionChart: React.VFC<ChargingSessionChartProps> = ({ data }) => {
  const { t } = useCustomTranslation()
  const { palette } = useTheme()

  const hasDischargeEnergy = data.some(item => Number(item.discharged.toFixed(2)) > 0)

  const legendFormatter = useCallback(
    (dataKey: string): string => {
      switch (dataKey) {
        case 'total':
          return t('emobility.chargingSession.totalEnergyCharged')
        case 'solar':
          return t('emobility.chargingSession.totalSolarEnergyCharged')
        case 'discharged':
          return t('emobility.chargingSession.totalEnergyDischarged')
        default:
          return dataKey
      }
    },
    [t]
  )
  return (
    <ResponsiveContainer width="100%" height={400}>
      <BarChart
        margin={{
          top: 32,
        }}
        // BarChart requires at least one data point in order to display a valid, but empty chart, so mock it here
        data={data.length === 0 ? [{ total: 0, solar: 0, discharged: 0, date: '' }] : data}
      >
        <CartesianGrid vertical={false} />
        <XAxis
          dataKey="date"
          minTickGap={6 * 20}
          interval="preserveStartEnd"
          tickSize={10}
          fontSize={11}
          padding={{ left: 12, right: 12 }}
          tickFormatter={date => (date ? format(date, t('common.format.date-fns-date')) : '')}
        />
        <YAxis
          tickSize={10}
          fontSize={11}
          unit={` ${UnitTypes.kiloWattHour}`}
          tickFormatter={value => {
            return getLocalizedNumber(value)
          }}
        />
        <Tooltip content={ChargingSessionChartTooltip} />
        <Legend align="right" verticalAlign="top" wrapperStyle={{ width: 'auto', marginTop: -8 }} formatter={legendFormatter} />
        <Bar dataKey="total" fill={palette.chart.totalGridChargedBarV1} />
        <Bar dataKey="solar" fill={palette.chart.solarChargedBarV1} />
        {hasDischargeEnergy && <Bar dataKey="discharged" fill={palette.chart.totalDischargedEnergyBackgroundV1} />}
      </BarChart>
    </ResponsiveContainer>
  )
}

const ChargingSessionChartTooltip: React.VFC<TooltipProps<number, string>> = ({ active, payload, label: date }): React.ReactElement => {
  const { classes, cx } = useStyles()
  const { t } = useCustomTranslation()

  const formatValue = useCallback((value: number | undefined): string => {
    const formattedValue = `${value ? getLocalizedNumber(value, 2) : 0} ${UnitTypes.kiloWattHour}`

    return formattedValue
  }, [])

  if (!payload || payload?.length === 0 || !active) {
    return <></>
  }

  /* Payload is something that comes from recharts library
   * It is built in native Tooltip component, in TS as TooltipProps
   * Payload always comes as array, so all tooltip items must be taken from array
   * For more information, please visit https://recharts.org/en-US/api/Tooltip
   */
  const totalChargedTooltip = formatValue(payload[0].value)
  const solarChargedTooltip = formatValue(payload[1].value)
  const dischargedTooltip = payload[2] && formatValue(payload[2].value)

  return (
    <Paper sx={theme => ({ borderRadius: 2, padding: theme.spacing(2), paddingTop: 1 })}>
      <Typography variant="overline">{date ? format(date, t('common.format.date-fns-date')) : t('common.noDataShort')}</Typography>

      <Stack direction="row" spacing={1}>
        <Box className={cx(classes.tooltipIndicator, classes.tooltipIndicatorTotal)} />
        <Typography variant="caption">{totalChargedTooltip}</Typography>
      </Stack>

      <Stack direction="row" spacing={1}>
        <Box className={cx(classes.tooltipIndicator, classes.tooltipIndicatorSolar)} />
        <Typography variant="caption">{solarChargedTooltip}</Typography>
      </Stack>
      {dischargedTooltip && (
        <Stack direction="row" spacing={1}>
          <Box className={cx(classes.tooltipIndicator, classes.tooltipIndicatorDischarge)} />
          <Typography variant="caption">{dischargedTooltip}</Typography>
        </Stack>
      )}
    </Paper>
  )
}

export default ChargingSessionChart
